import { useEffect, useRef, useState } from "react";
import { canSpeak, speak } from "../lib/text.js";

/**
 * Some engines never report the end of a word. After this long the echo goes
 * anyway, so a button cannot stay lit for good.
 */
const ECHO_MAX_MS = 5000;

/**
 * Reads a word on tap and says, visibly, that it is reading (#98). On a muted
 * iPad nothing is heard (docs/UX.md §4), but the button still answers the
 * tap: the app did its part, and the silence is the device.
 */
export function useSpeak(): { speaking: boolean; say: (text: string, lang: string) => void } {
  const [speaking, setSpeaking] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  // Starting a word cancels the one before, and the engine then ends that one
  // late: only the newest word may switch the echo off.
  const generation = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const say = (text: string, lang: string) => {
    const id = ++generation.current;
    window.clearTimeout(timer.current);
    setSpeaking(true);
    const done = () => {
      if (generation.current !== id) return;
      window.clearTimeout(timer.current);
      setSpeaking(false);
    };
    timer.current = window.setTimeout(done, ECHO_MAX_MS);
    speak(text, lang, done);
  };
  return { speaking, say };
}

/**
 * Reads one word aloud. Hidden where the browser has no speech at all, so a
 * child never taps a button that does nothing. A word in German is not read:
 * the point is how the foreign word sounds.
 */
export function SpeakButton({
  text,
  lang,
  label,
  size = "md",
}: {
  text: string;
  lang: string;
  label: string;
  size?: "sm" | "md";
}) {
  const { speaking, say } = useSpeak();
  if (!canSpeak() || lang === "de") return null;
  const box = size === "sm" ? "min-h-11 min-w-11 rounded-xl" : "min-h-12 min-w-12 rounded-2xl";
  // A calm echo, not a status: the brand colour, no motion (docs/PLAN.md §0).
  const tone = speaking ? "border-fox bg-fox/10 text-fox-ink" : "border-line bg-paper-2 text-ink-soft";
  return (
    <button
      type="button"
      className={`flex shrink-0 items-center justify-center border hover:text-fox-ink ${tone} ${box}`}
      onClick={() => say(text, lang)}
      aria-label={label}
      data-speaking={speaking || undefined}
    >
      <svg viewBox="0 0 24 24" fill="none" className={size === "sm" ? "h-5 w-5" : "h-6 w-6"} aria-hidden>
        <path
          d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M15.5 9.2a4 4 0 0 1 0 5.6M18 6.7a7.5 7.5 0 0 1 0 10.6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
